import { Box, Button, makeStyles } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmationDialog from '../../../../../../components/ConfirmationDialog';
import { useAuth } from '../../../../../../contexts/AuthContext/AuthContext';
import { assignCompanyAsTestedParty } from '../../../../../../core/api/benchmark';
import { CONSTANTS } from '../../../../../../core/config/constants';
import { FontSizes, ResolutionColors } from '../../../../../../core/config/mainTheme';
import { hasBenchwiseAccess } from '../../../../../../core/utils/access.utils';
import { toCssValue } from '../../../../../../core/utils/theme.utils';
import { BenchmarkCompanyView } from '../../../../../../generated/model/benchmarkCompanyView';
import { ResolveBenchmarkCompanyRequest } from '../../../../../../generated/model/resolveBenchmarkCompanyRequest';
import { useErrorsDispatch } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkDispatch, useBenchmarkState } from '../../../../Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../../Context/BenchmarkContext.types';
import BenchwiseValidateModal from './BenchwiseValidateModal';
import ResolutionIcon from './ResolutionIcon';
import ResolutionModal from './ResolutionModal';

const ResolutionPanelCM: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [showBenchmarkEditModal, setShowBenchmarkEditModal] = useState(false);
  const [showBenchwiseValidateModal, setShowBenchwiseValidateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [resolutionStatus, setResolutionStatus] = useState(
    'ACCEPTED' as ResolveBenchmarkCompanyRequest.StatusEnum,
  );
  const { user } = useAuth();
  const allowBenchwise = hasBenchwiseAccess(user);
  const {
    companies,
    activeCompanyId,
    pagination,
    activeBenchmark,
    benchwiseState,
    testedParty,
  } = useBenchmarkState();
  const dispatchBenchmark = useBenchmarkDispatch();
  const dispatchErrors = useErrorsDispatch();
  const activeCompany: BenchmarkCompanyView | undefined = companies.find(
    (c) => activeCompanyId === c.companyInfo?.id,
  );
  const [openConfDialog, setConfDialog] = useState(false);
  const testedPartyEnabled = !(window as any)?.env?.DISABLE_TESTED_PARTY;

  const openNextCompany = () => {
    const currentCompanyIdx = companies.findIndex((c) => activeCompanyId === c.companyInfo?.id);
    if (currentCompanyIdx < 0) return;

    const nextCompanyIdx = currentCompanyIdx + 1;

    if (nextCompanyIdx < companies.length) {
      const nextCompanyId = companies[nextCompanyIdx]?.companyInfo?.id;
      if (!nextCompanyId) return;
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetActiveBenchmarkCompanyId,
        payload: nextCompanyId,
      });
    } else if (pagination.totalPages > pagination.page) {
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetActiveBenchmarkCompanyId,
        payload: '',
      });
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetPagination,
        payload: { ...pagination, page: pagination.page + 1 },
      });
    }
  };

  const assignAsTestedParty = async () => {
    try {
      if (activeCompanyId) {
        await assignCompanyAsTestedParty(activeBenchmark?.id!, activeCompanyId);
        dispatchBenchmark({
          type: BenchmarkContextActionTypes.SetTestedParty,
          payload: { uploaded: true, companyId: activeCompanyId },
        });
      }
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: {
          error,
          includeDetails: error?.data?.error === 'UNABLE_TO_ASSIGN_COMPANY_AS_TESTED_PARTY',
        },
      });
    }
  };

  function title() {
    if (isEditing) {
      return t('benchmark:resolveBenchmarkCompany.commentEditTitle');
    }
    switch (resolutionStatus) {
      case 'ACCEPTED':
        return t('benchmark:resolveBenchmarkCompany.acceptModalTitle');
      case 'REJECTED':
        return t('benchmark:resolveBenchmarkCompany.rejectModalTitle');
      case 'POSTPONED':
        return t('benchmark:resolveBenchmarkCompany.reviewModalTitle');
    }
  }

  // Helper for benchwiseValidate state
  const isBenchwiseValidateDisabled =
    benchwiseState?.benchwiseStage === 'FAST_REVIEW' ||
    benchwiseState?.benchwiseStage === 'SLOW_REVIEW';

  // Helper for benchwise company limit
  const benchwiseCompanyLimitReached =
    (activeBenchmark?.totalCount ?? 0) > CONSTANTS.MAX_DEFAULT_VERIFY_COMPANIES;

  return (
    <>
      <Box className={classes.headerPanel}>
        <Button
          onClick={() => {
            setResolutionStatus('ACCEPTED');
            setShowBenchmarkEditModal(true);
          }}
          startIcon={<ResolutionIcon icon={'checkMark'} />}
          variant="contained"
          className={classNames(classes.btn, classes.acceptBtn)}
        >
          {t('benchmark:resolveBenchmarkCompany.accept')}
        </Button>
        <Button
          onClick={() => {
            setResolutionStatus('REJECTED');
            setShowBenchmarkEditModal(true);
          }}
          startIcon={<ResolutionIcon icon={'xMark'} />}
          variant="contained"
          className={classNames(classes.btn, classes.rejectBtn)}
        >
          {t('benchmark:resolveBenchmarkCompany.reject')}
        </Button>
        <Button
          onClick={() => {
            setResolutionStatus('POSTPONED');
            setShowBenchmarkEditModal(true);
          }}
          startIcon={<ResolutionIcon icon={'clockMark'} />}
          variant="contained"
          className={classNames(classes.btn, classes.reviewBtn)}
        >
          {t('benchmark:resolveBenchmarkCompany.review')}
        </Button>
        {allowBenchwise && (
          <Button
            onClick={() => {
              setShowBenchwiseValidateModal(true);
            }}
            color="primary"
            variant="contained"
            className={classes.btn}
            disabled={isBenchwiseValidateDisabled || benchwiseCompanyLimitReached}
          >
            {t('benchmark:resolveBenchmarkCompany.benchwiseValidate')}
          </Button>
        )}
        {testedPartyEnabled && testedParty?.companyId === activeCompanyId && (
          <span className={classes.testedParty}>
            {t('benchmark:resolveBenchmarkCompany.assignedAsTP')}
          </span>
        )}
        {testedPartyEnabled && testedParty?.companyId !== activeCompanyId && (
          <>
            <Button
              onClick={() => (testedParty?.uploaded ? setConfDialog(true) : assignAsTestedParty())}
              color="primary"
              variant="contained"
              className={classes.btn}
            >
              {t('benchmark:resolveBenchmarkCompany.assignAsTP')}
            </Button>
            <ConfirmationDialog
              open={openConfDialog}
              handleClose={() => {
                setConfDialog(false);
              }}
              handleConfirmOk={() => {
                assignAsTestedParty();
                setConfDialog(false);
              }}
              text="app:confirmationText:benchmark:overrideTestedParty"
            />
          </>
        )}{' '}
        {activeCompany?.benchmarkInfo?.comment != null && (
          <Button
            onClick={() => {
              setResolutionStatus(
                activeCompany?.benchmarkInfo?.status
                  ? activeCompany.benchmarkInfo.status
                  : 'ACCEPTED',
              );
              setIsEditing(true);
              setShowBenchmarkEditModal(true);
            }}
            variant="text"
            className={classNames([classes.btn, classes.commentBtn])}
            startIcon={<EditIcon />}
          >
            {activeCompany.benchmarkInfo.comment}
          </Button>
        )}
        <ResolutionModal
          title={title()}
          resolutionStatus={resolutionStatus}
          show={showBenchmarkEditModal}
          isEditing={isEditing}
          comment={activeCompany?.benchmarkInfo?.comment}
          handleSave={() => {
            setIsEditing(false);
            setShowBenchmarkEditModal(false);
            dispatchBenchmark({ type: BenchmarkContextActionTypes.IncrementRefreshKey });
            if (!isEditing) {
              openNextCompany();
            }
          }}
          handleClose={() => {
            setIsEditing(false);
            setShowBenchmarkEditModal(false);
          }}
        />
        <BenchwiseValidateModal
          title={t('benchmark:resolveBenchmarkCompany.benchwiseValidateTitle')}
          validationType={'BENCHWISE_FAST'}
          show={showBenchwiseValidateModal}
          comment={null}
          handleSave={() => {
            setShowBenchwiseValidateModal(false);
            dispatchBenchmark({ type: BenchmarkContextActionTypes.IncrementRefreshKey });
            if (!isEditing) {
              openNextCompany();
            }
          }}
          handleClose={() => {
            setShowBenchwiseValidateModal(false);
          }}
        />
      </Box>
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  btn: {
    textTransform: 'none',
    fontWeight: FontSizes.fontWeight.bold,
    marginRight: theme.spacing(2),
  },

  acceptBtn: {
    backgroundColor: ResolutionColors.Accepted,
    '&:hover': {
      backgroundColor: ResolutionColors.AcceptedHover,
    },
    color: '#FFFFFF',
  },
  rejectBtn: {
    backgroundColor: ResolutionColors.Rejected,
    '&:hover': {
      backgroundColor: ResolutionColors.RejectedHover,
    },
    color: '#FFFFFF',
  },
  reviewBtn: {
    backgroundColor: ResolutionColors.Pending,
    '&:hover': {
      backgroundColor: ResolutionColors.PendingHover,
    },
    color: '#212121',
  },

  commentBtn: {
    marginLeft: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  headerPanel: {
    display: 'flex',
    flexWrap: 'wrap',
    overflow: 'hidden',
    marginTop: theme.spacing(6),
  },
  testedParty: {
    fontSize: FontSizes.size.medium,
    fontWeight: FontSizes.fontWeight.bold,
    backgroundColor: theme.palette.secondary.main,
    padding: toCssValue([theme.spacing(3), theme.spacing(4)]),
  },
}));

export default ResolutionPanelCM;
